import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import {Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useAgents } from "../../hooks/useAgents";
import { AgentsFormSchema } from "../../validations/schemas";
import { AGENTS_STATUS_OPTIONS, infoAgentsFields } from "../../constants";
import { AppContext } from "../../context/index";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";

interface NewAgentsProps {
  update?: boolean;
}

const agentDefaultValues = {
  name: "",
  url: "",
  version: "",
  publickey: "",
  status: "DRAFT",
};

export const NewAgentPage: React.FC<NewAgentsProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedAgent },
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update ? 
      {
        id: selectedAgent?.id,
        name: selectedAgent?.name,
        version: selectedAgent?.version,
        url: selectedAgent?.url,
        publickey: selectedAgent?.publickey,
        status: selectedAgent?.status,
        updatedby: user?.name
      } : 
      {
        ...agentDefaultValues,
        createdby: user?.name
      }
  });

  const {
    getValues,
    formState: { isValid, isDirty },
  } = form;

  const { agentsAction } = useAgents("ACTIONS");
  const [agentsFields, setAgentsFields] = useState();
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const formKeysOptions = { 
    status: AGENTS_STATUS_OPTIONS(t).sort(
      (a, b) => a.label.localeCompare(b.label)
    )
  };

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await agentsAction(update ? "UPDATE" : "CREATE", [formValues]);
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Agents.${update ? "save" : "create"}ModalTitle`),
          content: t(`Agents.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Agents.cancelModalTitle"),
          content: t("Agents.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (update) {
      setAgentsFields(infoAgentsFields as any)
    } else {
      const keysToExtract = ['status'];
      const extractedObjects: any = infoAgentsFields.filter(obj => !keysToExtract.includes(obj.key))
      setAgentsFields(extractedObjects);
    }
  }, [update])

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("Agents.editTitle") : t("Agents.newAgentTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={`TransactionalSubheader.BreadCrumb.${t("Agents.agentsDatatableTitle")}`}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Agents.deleteThisTranslate"),
              disabled: true
            },
            {
              label: t("Agents.agentsDatatableTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
              ? t("Agents.editTitle")
              : t("Agents.newAgentTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>
      
      <div id='agents-customformcomponent'>
        <CustomFormComponent 
          functionality="Agents"
          fields={agentsFields}
          form={form}
          schemaOfValidation={AgentsFormSchema}
          dropdownOptions={update && formKeysOptions}
        />
      </div>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("Agents.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            variant="p-button"
            label={
              update 
                ? t("Agents.saveBtnLabel")
                : t("Agents.createBtnLabel")
            }
            disabled={update ? !(isValid && isDirty) : !isValid}
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
